// src/components/eventos/components/EventoForm.js
import React, { useState, useEffect } from 'react';
import {
    useToast,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    useDisclosure,
} from '@chakra-ui/react';
import TarefaForm from './TarefaForm';
import TarefasList from './TarefasList';

const ordenarTarefasPorHorario = (tarefas) => [...tarefas].sort((a, b) => {
    const [hA, mA] = a.horaInicio.split(':').map(Number);
    const [hB, mB] = b.horaInicio.split(':').map(Number);
    return (hA * 60 + mA) - (hB * 60 + mB);
});

const temSobreposicao = (tarefas, novaTarefa, ignorarId = null) => {
    const toMin = (h) => { const [hh, mm] = h.split(':').map(Number); return hh * 60 + mm; };
    const nI = toMin(novaTarefa.horaInicio), nF = toMin(novaTarefa.horaFim);
    return tarefas.filter(t => t.id !== ignorarId).some(t => nI < toMin(t.horaFim) && nF > toMin(t.horaInicio));
};

/* ---- SVG Icons ---- */
const SaveIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
    </svg>
);
const BookmarkIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
);
const FolderIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
);
const CopyIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
);

const EventoForm = ({ supabase, session, eventoEditando, onEventoSalvo, onCancelar }) => {
    const [evento, setEvento] = useState({
        titulo: '', data: new Date().toISOString().split('T')[0],
        local: '', descricao: '', modo_automatico: false, tarefas: [],
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tarefaEditando, setTarefaEditando] = useState(null);
    const [templates, setTemplates] = useState([]);
    const toast = useToast();
    const { isOpen: isTemplateOpen, onOpen: onTemplateOpen, onClose: onTemplateClose } = useDisclosure();

    useEffect(() => {
        if (eventoEditando) {
            setEvento({
                titulo: eventoEditando.titulo, data: eventoEditando.data,
                local: eventoEditando.local || '', descricao: eventoEditando.descricao || '',
                modo_automatico: eventoEditando.modo_automatico || false, tarefas: eventoEditando.tarefas || [],
            });
        }
    }, [eventoEditando]);

    const resolverClienteId = async () => {
        const id = session.user.user_metadata?.cliente_id || session.user.app_metadata?.cliente_id;
        if (id) return id;
        const { data } = await supabase
            .from('usuarios_sistema')
            .select('cliente_id')
            .eq('auth_user_id', session.user.id)
            .single();
        if (!data?.cliente_id) throw new Error('Sessão sem cliente vinculado. Faça logout e entre novamente.');
        return data.cliente_id;
    };

    const validate = () => {
        const errs = {};
        if (!evento.titulo.trim()) errs.titulo = 'Título é obrigatório';
        if (!evento.data) errs.data = 'Data é obrigatória';
        if (evento.tarefas.length === 0) errs.tarefas = 'Adicione pelo menos uma tarefa ao evento';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleAdicionarTarefa = (nova) => {
        if (temSobreposicao(evento.tarefas, nova)) {
            toast({ title: 'Conflito de horário', description: `O horário ${nova.horaInicio}–${nova.horaFim} conflita com outra tarefa.`, status: 'error', duration: 4000, isClosable: true });
            return;
        }
        const conc = evento.tarefas.filter(t => t.status === 'concluida');
        const naoConc = evento.tarefas.filter(t => t.status !== 'concluida');
        setEvento(p => ({ ...p, tarefas: [...conc, ...ordenarTarefasPorHorario([...naoConc, nova])] }));
        setErrors(p => ({ ...p, tarefas: undefined }));
        toast({ title: 'Tarefa adicionada', status: 'success', duration: 2000, isClosable: true });
    };

    const handleAtualizarTarefa = (atualizada) => {
        if (temSobreposicao(evento.tarefas, atualizada, atualizada.id)) {
            toast({ title: 'Conflito de horário', description: `O horário ${atualizada.horaInicio}–${atualizada.horaFim} conflita com outra tarefa.`, status: 'error', duration: 4000, isClosable: true });
            return;
        }
        const atualizadas = evento.tarefas.map(t => t.id === atualizada.id ? atualizada : t);
        const conc = atualizadas.filter(t => t.status === 'concluida');
        const naoConc = atualizadas.filter(t => t.status !== 'concluida');
        setEvento(p => ({ ...p, tarefas: [...conc, ...ordenarTarefasPorHorario(naoConc)] }));
        setTarefaEditando(null);
        toast({ title: 'Tarefa atualizada', status: 'success', duration: 2000, isClosable: true });
    };

    const handleRemoverTarefa = (id) => {
        if (evento.tarefas.find(t => t.id === id)?.status === 'concluida') {
            toast({ title: 'Tarefa já concluída', description: 'Não é possível remover tarefas concluídas', status: 'warning', duration: 3000, isClosable: true });
            return;
        }
        setEvento(p => ({ ...p, tarefas: p.tarefas.filter(t => t.id !== id) }));
        if (tarefaEditando?.id === id) setTarefaEditando(null);
        toast({ title: 'Tarefa removida', status: 'info', duration: 2000, isClosable: true });
    };

    const handleEditarTarefa = (tarefa) => {
        if (tarefa.status === 'concluida') {
            toast({ title: 'Tarefa já concluída', description: 'Não é possível editar tarefas concluídas', status: 'warning', duration: 3000, isClosable: true });
            return;
        }
        setTarefaEditando(tarefa);
    };

    const handleAbrirTemplates = async () => {
        try {
            const clienteId = await resolverClienteId();
            if (!clienteId) return;
            const { data, error } = await supabase.from('eventos')
                .select('id, titulo, template_nome, tarefas, local, descricao')
                .eq('cliente_id', clienteId).eq('is_template', true)
                .order('created_at', { ascending: false });
            if (error) throw error;
            setTemplates(data || []);
            onTemplateOpen();
        } catch (error) {
            toast({ title: 'Erro ao carregar templates', description: error.message, status: 'error', duration: 3000 });
        }
    };

    const handleUsarTemplate = (template) => {
        setEvento(p => ({
            ...p, local: template.local || '', descricao: template.descricao || '',
            tarefas: (template.tarefas || []).map(t => ({ ...t, id: crypto.randomUUID(), status: 'pendente', horaInicioReal: null, horaFimReal: null })),
        }));
        onTemplateClose();
        toast({ title: `Template "${template.template_nome || template.titulo}" aplicado!`, status: 'success', duration: 2000 });
    };

    const handleSalvarComoTemplate = async () => {
        if (!evento.titulo.trim()) { toast({ title: 'Informe o título para salvar como template', status: 'warning', duration: 3000 }); return; }
        if (evento.tarefas.length === 0) { toast({ title: 'Adicione pelo menos uma tarefa ao template', status: 'warning', duration: 3000 }); return; }
        setIsSubmitting(true);
        try {
            const clienteId = await resolverClienteId();
            const { error } = await supabase.from('eventos').insert([{
                titulo: evento.titulo.trim(), data: new Date().toISOString().split('T')[0],
                local: evento.local.trim(), descricao: evento.descricao.trim(), tarefas: evento.tarefas,
                cliente_id: clienteId, user_id: session.user.id, status: 'agendado',
                is_template: true, template_nome: evento.titulo.trim(),
            }]);
            if (error) throw error;
            toast({ title: 'Salvo como template!', description: 'Disponível ao criar novos eventos.', status: 'success', duration: 3000 });
        } catch (error) {
            toast({ title: 'Erro ao salvar template', description: error.message, status: 'error', duration: 5000 });
        } finally { setIsSubmitting(false); }
    };

    const handleSalvar = async () => {
        if (!validate()) {
            toast({ title: 'Formulário incompleto', description: 'Preencha todos os campos obrigatórios', status: 'error', duration: 3000, isClosable: true });
            return;
        }
        setIsSubmitting(true);
        try {
            const clienteId = await resolverClienteId();
            const eventoData = {
                titulo: evento.titulo.trim(), data: evento.data,
                local: evento.local.trim(), descricao: evento.descricao.trim(),
                modo_automatico: evento.modo_automatico, tarefas: evento.tarefas,
                cliente_id: clienteId, user_id: session.user.id,
            };
            if (eventoEditando) {
                const { error } = await supabase.from('eventos').update(eventoData).eq('id', eventoEditando.id);
                if (error) throw error;
            } else {
                const { error } = await supabase.from('eventos').insert([{ ...eventoData, status: 'agendado' }]);
                if (error) throw error;
            }
            onEventoSalvo();
        } catch (error) {
            toast({ title: 'Erro ao salvar evento', description: error.message, status: 'error', duration: 5000, isClosable: true });
        } finally { setIsSubmitting(false); }
    };

    const inputStyle = {
        width: '100%', padding: '10px 12px', borderRadius: '9px',
        background: '#f8fafc', border: '1px solid #e2e8f0',
        color: '#1e293b', fontSize: '14px', fontFamily: 'inherit', outline: 'none',
        boxSizing: 'border-box', transition: 'border-color 0.15s',
    };
    const labelStyle = { display: 'block', marginBottom: '7px', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' };
    const sectionStyle = { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px' };

    return (
        <>
            <style>{`
                .ef-input:focus { border-color: #2CCB90 !important; background: #f0fdf8 !important; }
                .ef-input::placeholder { color: #94a3b8; }
            `}</style>

            <div style={{ padding: '24px', fontFamily: "'Inter', -apple-system, sans-serif", display: 'flex', flexDirection: 'column', gap: '20px' }}>

                {/* Botão template */}
                {!eventoEditando && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={handleAbrirTemplates} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 16px', borderRadius: '9px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', color: '#7c3aed', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                            <FolderIcon /> Usar Template
                        </button>
                    </div>
                )}

                {/* Seção: Informações do Evento */}
                <div style={sectionStyle}>
                    <h3 style={{ margin: '0 0 18px', fontSize: '14px', fontWeight: 700, color: '#2CCB90' }}>Informações do Evento</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <div>
                            <label style={labelStyle}>Título do Evento *</label>
                            <input className="ef-input" style={{ ...inputStyle, borderColor: errors.titulo ? '#fca5a5' : '#e2e8f0' }}
                                value={evento.titulo}
                                onChange={e => { setEvento(p => ({ ...p, titulo: e.target.value })); setErrors(p => ({ ...p, titulo: undefined })); }}
                                placeholder="Ex: Culto de Celebração, Conferência Anual..." />
                            {errors.titulo && <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#ef4444' }}>{errors.titulo}</p>}
                        </div>

                        <div>
                            <label style={labelStyle}>Data do Evento *</label>
                            <input className="ef-input" style={{ ...inputStyle, borderColor: errors.data ? '#fca5a5' : '#e2e8f0' }}
                                type="date" value={evento.data}
                                onChange={e => { setEvento(p => ({ ...p, data: e.target.value })); setErrors(p => ({ ...p, data: undefined })); }} />
                            {errors.data && <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#ef4444' }}>{errors.data}</p>}
                        </div>

                        <div>
                            <label style={labelStyle}>Local (opcional)</label>
                            <input className="ef-input" style={inputStyle} value={evento.local}
                                onChange={e => setEvento(p => ({ ...p, local: e.target.value }))}
                                placeholder="Ex: Auditório Principal, Salão da Igreja..." />
                        </div>

                        <div>
                            <label style={labelStyle}>Descrição (opcional)</label>
                            <textarea className="ef-input" style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
                                value={evento.descricao}
                                onChange={e => setEvento(p => ({ ...p, descricao: e.target.value }))}
                                placeholder="Adicione uma descrição para o evento..." />
                        </div>

                        {/* Programa Automático */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', padding: '14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                            <div>
                                <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>Programa Automático</p>
                                <p style={{ margin: 0, fontSize: '12px', color: '#64748b', maxWidth: '420px' }}>
                                    Quando ativado, as tarefas avançam automaticamente conforme os horários programados.
                                </p>
                            </div>
                            <button
                                onClick={() => setEvento(p => ({ ...p, modo_automatico: !p.modo_automatico }))}
                                style={{ width: '44px', height: '24px', borderRadius: '12px', background: evento.modo_automatico ? '#2CCB90' : '#e2e8f0', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                            >
                                <span style={{ position: 'absolute', top: '3px', left: evento.modo_automatico ? '23px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', display: 'block' }} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Divisor */}
                <div style={{ height: '1px', background: '#e2e8f0' }} />

                {/* Seção: Programação */}
                <div>
                    <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 700, color: '#2CCB90' }}>Programação do Evento</h3>

                    {errors.tarefas && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: '#fff5f5', border: '1px solid #fca5a5', borderRadius: '9px', marginBottom: '14px', color: '#ef4444', fontSize: '13px' }}>
                            {errors.tarefas}
                        </div>
                    )}

                    <TarefaForm
                        tarefaEditando={tarefaEditando}
                        onAdicionarTarefa={handleAdicionarTarefa}
                        onAtualizarTarefa={handleAtualizarTarefa}
                        onCancelarEdicao={() => setTarefaEditando(null)}
                    />

                    {evento.tarefas.length > 0 && (
                        <div style={{ marginTop: '16px' }}>
                            <TarefasList
                                tarefas={evento.tarefas}
                                tarefaEditando={tarefaEditando}
                                onEditarTarefa={handleEditarTarefa}
                                onRemoverTarefa={handleRemoverTarefa}
                            />
                        </div>
                    )}
                </div>

                {/* Divisor */}
                <div style={{ height: '1px', background: '#e2e8f0' }} />

                {/* Botões de Ação */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button onClick={onCancelar} disabled={isSubmitting}
                        style={{ padding: '10px 20px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Cancelar
                    </button>
                    <button onClick={handleSalvarComoTemplate} disabled={isSubmitting}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 20px', borderRadius: '10px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', color: '#7c3aed', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <BookmarkIcon /> Salvar como Template
                    </button>
                    <button onClick={handleSalvar} disabled={isSubmitting}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 24px', borderRadius: '10px', background: isSubmitting ? 'rgba(44,203,144,0.3)' : 'linear-gradient(135deg, #2CCB90, #22a876)', border: 'none', color: '#fff', fontSize: '14px', fontWeight: 700, cursor: isSubmitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit', boxShadow: isSubmitting ? 'none' : '0 4px 16px rgba(44,203,144,0.25)' }}>
                        <SaveIcon /> {isSubmitting ? 'Salvando...' : eventoEditando ? 'Atualizar Evento' : 'Criar Evento'}
                    </button>
                </div>
            </div>

            {/* Modal Templates */}
            <Modal isOpen={isTemplateOpen} onClose={onTemplateClose} size="lg">
                <ModalOverlay bg="rgba(0,0,0,0.4)" backdropFilter="blur(8px)" />
                <ModalContent bg="#ffffff" border="1px solid #e2e8f0" borderRadius="16px" color="#1e293b" boxShadow="0 20px 60px rgba(0,0,0,0.15)">
                    <ModalHeader fontSize="16px" fontWeight="700" pb={2} color="#0f172a">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FolderIcon /> Escolher Template
                        </div>
                    </ModalHeader>
                    <ModalCloseButton color="#94a3b8" />
                    <ModalBody>
                        {templates.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
                                <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔖</div>
                                <p style={{ margin: '0 0 6px', fontSize: '14px', fontWeight: 600, color: '#64748b' }}>Nenhum template salvo ainda.</p>
                                <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>Crie um evento e clique em "Salvar como Template".</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {templates.map(t => (
                                    <div key={t.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                        <div>
                                            <p style={{ margin: '0 0 6px', fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{t.template_nome || t.titulo}</p>
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                <span style={{ padding: '2px 8px', borderRadius: '6px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)', color: '#7c3aed', fontSize: '11px' }}>
                                                    {t.tarefas?.length || 0} tarefa(s)
                                                </span>
                                                {t.local && <span style={{ fontSize: '12px', color: '#94a3b8' }}>📍 {t.local}</span>}
                                            </div>
                                        </div>
                                        <button onClick={() => handleUsarTemplate(t)}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '9px', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)', color: '#7c3aed', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
                                            <CopyIcon /> Usar
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter pt={2}>
                        <button onClick={onTemplateClose} style={{ padding: '8px 18px', borderRadius: '9px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Fechar
                        </button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default EventoForm;
